import { useEffect, useState } from 'react';
import { Plus, Send, BarChart2, X } from 'lucide-react';

interface Campaign {
  id: string;
  name: string;
  template_id: string;
  segment: any;
  schedule_type: string;
  schedule_days: number[] | null;
  status: string;
  created_at: string;
  template?: {
    id: string;
    name: string;
    subject: string;
  };
}

interface Template {
  id: string;
  name: string;
  subject: string;
}

interface Segment {
  id: string;
  name: string;
  description: string;
  filters: Record<string, any>;
}

interface CampaignStats {
  total: number;
  sent: number;
  opened: number;
  clicked: number;
  converted: number;
  openRate: string;
  clickRate: string;
  conversionRate: string;
}

export default function CampanhasEmail() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [stats, setStats] = useState<CampaignStats | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    template_id: '',
    segment_id: '',
    schedule_type: 'immediate',
    status: 'draft'
  });

  useEffect(() => {
    loadCampaigns();
    loadTemplates();
    loadSegments();
  }, []);

  const loadCampaigns = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      
      const res = await fetch(`${apiUrl}/api/email/campaigns`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        setCampaigns(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      
      const res = await fetch(`${apiUrl}/api/email/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        setTemplates(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
    }
  };

  const loadSegments = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      
      const res = await fetch(`${apiUrl}/api/campaign-segments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        setSegments(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar segmentos:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      
      const payload = {
        name: formData.name,
        template_id: formData.template_id,
        segment_id: formData.segment_id,
        schedule_type: formData.schedule_type,
        status: formData.status
      };

      const res = await fetch(`${apiUrl}/api/email/campaigns`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      
      if (data.success) {
        setShowModal(false);
        setFormData({
          name: '',
          template_id: '',
          segment_id: '',
          schedule_type: 'immediate',
          status: 'draft'
        });
        loadCampaigns();
      }
    } catch (error) {
      console.error('Erro ao criar campanha:', error);
    }
  };

  const handleSendCampaign = async (id: string) => {
    if (!confirm('Tem certeza que deseja enviar esta campanha?')) return;

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      
      const res = await fetch(`${apiUrl}/api/email/campaigns/${id}/send`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await res.json();
      
      if (data.success) {
        alert(data.message);
        loadCampaigns();
      }
    } catch (error) {
      console.error('Erro ao enviar campanha:', error);
    }
  };

  const loadStats = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      
      const res = await fetch(`${apiUrl}/api/email/campaigns/${id}/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const openStatsModal = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setShowStatsModal(true);
    loadStats(campaign.id);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">Rascunho</span>;
      case 'sending':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">Enviando</span>;
      case 'sent':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Enviada</span>;
      default:
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando campanhas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Campanhas de Email</h1>
          <p className="text-gray-600">Crie e gerencie suas campanhas de email marketing</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 flex items-center"
        >
          <Plus className="h-5 w-5 mr-2" />
          Nova Campanha
        </button>
      </div>

      {/* Lista de Campanhas */}
      <div className="grid grid-cols-1 gap-4">
        {campaigns.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-500">Nenhuma campanha encontrada</p>
          </div>
        ) : (
          campaigns.map(campaign => (
            <div key={campaign.id} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{campaign.name}</h3>
                    {getStatusBadge(campaign.status)}
                  </div>
                  
                  {campaign.template && (
                    <p className="text-sm text-gray-600 mb-2">
                      Template: {campaign.template.name}
                    </p>
                  )}
                  
                  {campaign.segment && (
                    <p className="text-sm text-gray-500">
                      Segmento: {JSON.stringify(campaign.segment)}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openStatsModal(campaign)}
                    className="px-3 py-2 text-sm bg-purple-500 text-white rounded-md hover:bg-purple-600 flex items-center"
                  >
                    <BarChart2 className="h-4 w-4 mr-1" />
                    Estatísticas
                  </button>
                  {campaign.status === 'draft' && (
                    <button
                      onClick={() => handleSendCampaign(campaign.id)}
                      className="px-3 py-2 text-sm bg-green-500 text-white rounded-md hover:bg-green-600 flex items-center"
                    >
                      <Send className="h-4 w-4 mr-1" />
                      Enviar
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal de Criar Campanha */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Nova Campanha</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nome da Campanha *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Template *</label>
                  <select
                    value={formData.template_id}
                    onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  >
                    <option value="">Selecione um template</option>
                    {templates.map(template => (
                      <option key={template.id} value={template.id}>{template.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Segmento *</label>
                  <select
                    value={formData.segment_id}
                    onChange={(e) => setFormData({ ...formData, segment_id: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  >
                    <option value="">Selecione um segmento</option>
                    {segments.map(segment => (
                      <option key={segment.id} value={segment.id}>
                        {segment.name}
                        {segment.description && ` - ${segment.description}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Agendamento</label>
                  <select
                    value={formData.schedule_type}
                    onChange={(e) => setFormData({ ...formData, schedule_type: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="immediate">Imediato</option>
                    <option value="scheduled">Agendado</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  Criar Campanha
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Estatísticas */}
      {showStatsModal && selectedCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Estatísticas: {selectedCampaign.name}</h2>
              <button onClick={() => setShowStatsModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="h-6 w-6" />
              </button>
            </div>

            {stats ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Total Enviados</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.sent}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Abertos</p>
                  <p className="text-2xl font-bold text-green-600">{stats.opened}</p>
                  <p className="text-xs text-gray-500">{stats.openRate}% taxa de abertura</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Cliques</p>
                  <p className="text-2xl font-bold text-purple-600">{stats.clicked}</p>
                  <p className="text-xs text-gray-500">{stats.clickRate}% taxa de clique</p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Conversões</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.converted}</p>
                  <p className="text-xs text-gray-500">{stats.conversionRate}% taxa de conversão</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-2 text-gray-600">Carregando estatísticas...</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
